import { LitElement, html, css, ifDefined, classMap, ref, createRef } from '@hh/global.js'

class HHCustomSelect extends LitElement {
  static properties = {
    options: {
      type: Object,
      default: []
    },
    name: {
      type: String,
    },
    active: {
      type: String,
    },
    lang: {
      type: Boolean,
    },
    position: {
      type: String,
    },
    placeholder: {
      type: String,
    },
    _selected: {
      state: true,
    },
    _active: {
      state: true,
    },
    _filterText: {
      type: String
    }
  }

  $input = createRef()

  constructor() {
    super()
    this.handleBodyClick = this.handleBodyClick.bind(this)
    this.uuid = Math.random() * (1000000 - 1) + 1
    this.position = 'bottom'
    this._filterText = ''
  }

  connectedCallback() {
    super.connectedCallback()
    this._selected = this.active
    document.body.addEventListener('click', this.handleBodyClick)
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    document.body.addEventListener('click', this.handleBodyClick)
  }

  handleBodyClick(e) {
    const path = e.composedPath()
    if (
      path.some((node) => {
        return node === this
      })
    ) {
      return true
    }

    this._active && this.toggle()
  }

  onFilterInput (e) {
    this._filterText = e.target.value
  }

  toggle(e = false) {
    if (e && e.type === 'keyup' && !~['ESCAPE'].indexOf(e.code.toUpperCase())) {
      return true
    }

    this._active = !this._active

    if (this._active) {
      setTimeout(() => {
        this.$input.value.focus()
      }, 300);
    } else {
      this._filterText = ''
    }
  }

  selectValue(e, option) {
    e.preventDefault()
    this._selected = option.value
    ;(this.querySelector('[slot="input"]') || {}).value = option.value
    this.dispatchEvent(
      new CustomEvent('change', {
        bubbles: true,
        composed: true,
        detail: {
          option
        },
      })
    )
    this.toggle()
  }

  get selectedLabel() {
    const option = this.options.find(
      (option) => option.value === this._selected
    )
    if (!option) return ''
    return option.label
  }

  sanitize(value) {
    return value.replace('&amp;', '&')
  }

  render() {
    return html`<div>
      <div class="container">
        <div id="${this.uuid}-label" class="label">
          <slot name="title"></slot>
          <span class="selected-label">${this.selectedLabel}</span>
        </div>
        <button
          @click=${this.toggle}
          type="button"
          class="button"
          aria-expanded="${this._active}"
          aria-controls="${this.uuid}-list"
          aria-describedby="${this.uuid}-label"
        >
          <span
            class="button__label"
          >${this.selectedLabel}</span>
          <slot name="icon"></slot>
        </button>
        <slot name="input"></slot>
        <hh-focus-trap active="${this._active}">
          <input
            type="text"
            placeholder="${this.placeholder}"
            @input="${this.onFilterInput}"
            class="${classMap({
              input: true,
              'is-active': this._active
            })}"
            value="${this._filterText}"
            ${ref(this.$input)}
          >

          <div class="${classMap({
            wrapper: true,
            'is-active': this._active,
            'position-top': this.position === 'top',
          })}">
            <ul
              id="${this.uuid}-list"
              class="${classMap({
                list: true
              })}"
              @keyup="${this.toggle}"
              aria-hidden="${!this._active}"
              role="list"
            >
              ${this.options.map((option) => {
                this.lang && (option.lang = option.value)
                return html`<li
                  class="${classMap({
                    option: true,
                    'is-active': option.value === this._selected,
                    'is-visible': (option.label.toLowerCase().includes(this._filterText.toLowerCase()))
                  })}"
                >
                  <a
                    @click="${(e) => this.selectValue(e, option)}"
                    class="link"
                    href="#"
                    hreflang="${ifDefined(option.lang)}"
                    lang="${ifDefined(option.lang)}"
                  >
                    ${option.image ? html`
                      <div class="media-wrapper">
                        <div class="media">
                          <img class="image" src="${option.image}">
                        </div>
                      </div>
                    `: ''}

                    ${this.sanitize(option.label)}
                  </a>
                </li>`
              })}
            </ul>
          </div>
        </hh-focus-trap>
      </div>
    </div>`
  }

  static styles = css`
    :host {
      width: 100%;
    }

    .container {
      position: relative;
    }

    .button {
      align-items: center;
      background: var(--hh-color-background, transparent);
      border: var(--hh-border-width, 1px) solid var(--hh-color-neutral);
      cursor: pointer;
      display: flex;
      font: var(--hh-body-font);
      height: var(--hh-height, 35px);
      justify-content: space-between;
      padding: 0 10px;
      width: 100%;
    }

    .button[aria-expanded="true"] ::slotted([slot='icon']) {
      transform: rotate(180deg);
    }

    .button__label {
      position: relative;
      color: var(--hh-color-subdued);
    }

    ::slotted([slot='button']),
    ::slotted([slot='icon']) {
      align-items: center;
      display: flex;
      height: 100%;
    }

    ::slotted([slot='icon']) {
      margin-left: 30px;
      width: 10px;
      z-index: 1;
    }

    .label {
      margin-bottom: var(--hh-space-xs, 1rem);
      display: flex;
    }

    .selected-label {
      color: var(--hh-color-subdued);
      margin-left: var(--hh-space-2xs);
    }

    ::slotted([slot='title']) {
      display: block;
      color: var(--hh-color-ink, currentColor);
      font: var(--hh-body-utility);
    }

    .input {
      position: absolute;
      box-sizing: border-box;
      left: 0;
      bottom: 0;
      width: 100%;
      height: var(--hh-height, 35px);
      padding: var(--hh-space-xs);
      display: none;
    }

    .input.is-active {
      display: block;
    }

    .wrapper {
      background-color: var(--hh-background, white);
      margin: 0;
      max-width: var(--hh-options-max-width, 22rem);
      min-width: var(--hh-options-min-width, 12rem);
      opacity: 0;
      padding: 1rem 0.5rem 1rem 1rem;
      position: absolute;
      transform: translateY(0);
      transition:
        visibility var(--hh-duration-default, 300ms)
          var(--hh-duration-default, 300ms),
        opacity var(--hh-duration-default, 300ms),
        transform var(--hh-duration-default, 300ms);
      visibility: hidden;
      width: 100%;
      z-index: 5;
      margin-top: var(--hh-space-2xs);
      box-sizing: border-box;
    }

    .wrapper.position-top {
      bottom: 100%;
    }

    .wrapper.is-active {
      opacity: 1;
      transform: translateY(0);
      transition:
        transform var(--hh-duration-default, 300ms) 1ms,
        opacity var(--hh-duration-default, 300ms) 1ms,
        visibility 1ms;
      visibility: visible;
    }

    .list {
      display: flex;
      flex-wrap: wrap;
      max-height: var(--hh-options-max-height, 19rem);
      min-height: var(--hh-options-min-height, 8.2rem);
      gap: 0.5rem 1rem;
      overflow-y: auto;
      margin: 0;
      padding: 0;
      box-sizing: border-box;
      width: 100%;
      list-style: none;
    }

    .list::-webkit-scrollbar {
      border-radius: var(--hh-button-radius);
      width: 0.3rem;
    }

    .list::-webkit-scrollbar-thumb {
      background: var(--hh-color-night);
      border-radius: var(--hh-button-radius);
    }

    .list::-webkit-scrollbar-track {
      background: var(--hh-color-neutral);
      border-radius: var(--hh-button-radius);
    }

    .option {
      width: calc(25% - 1rem);
      display: none;
    }

    .option.is-visible {
      display: block;
    }

    .link {
      display: flex;
      flex-direction: column;
      gap: var(--hh-space-2xs);
      color: inherit;
      padding: var(--hh-link-padding, 0);
      text-decoration: none;
      font: var(--hh-body-caption);
    }

    .media-wrapper {
      width: 100%;
      padding: 0.2rem;
      border-radius: var(--hh-button-radius);
      border: solid 0.2rem transparent;
    }

    .option:hover .media-wrapper,
    .option.is-active .media-wrapper{
      border-color: var(--hh-color-brand-tertiary);
    }

    .media {
      position: relative;
      padding-bottom: 125%;
    }

    .media img {
      position: absolute;
      width: 100%;
      height: 100%;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      object-fit: contain;
    }
  `
}

customElements.define('hh-custom-select', HHCustomSelect)
